/**
 * Theme config — kanonická paleta barev pro celou aplikaci.
 *
 * Single source of truth: zde definuj barvu jednou, použij přes CSS var
 * (např. `var(--bg-primary)`) v komponentách. Tailwind config je mapuje
 * na utility classes (`bg-background`, `text-primary`, atd.).
 *
 * Admin může runtime přepsat hodnoty přes `/admin/theme` (úložiště:
 * `content_pages` table, key=`theme-colors`, html=JSON s overrides).
 * `apply-theme.ts` při bootu načte z API a injektuje do `:root`.
 *
 * Pravidlo: VŠECHNY barvy v aplikaci musí jít přes `var(--...)`.
 * Hardcoded hex v JSX/CSS je bug — přidej sem novou var.
 */

export interface ThemeColor {
  /** CSS var name bez `--` prefixu (např. `bg-primary`). */
  name: string;
  /** Default HEX hodnota (např. `#000000`). */
  default: string;
  /** Lidsky čitelný popis pro admin UI. */
  label: string;
  /** Kategorie pro grouping v admin UI. */
  group: "background" | "text" | "accent" | "status" | "team" | "rank";
}

/**
 * Defaultní paleta. Pořadí = pořadí zobrazení v admin UI.
 *
 * Změna `default` zde = trvalá změna (po `pnpm dev` restart nebo build).
 * Změna v admin UI = runtime override (uložené v DB, prioritu má).
 */
export const THEME_COLORS: readonly ThemeColor[] = [
  // ── Backgrounds ──────────────────────────────────────────────────────────
  { name: "bg-primary",    default: "#000000", label: "Hlavní pozadí",       group: "background" },
  { name: "bg-card",       default: "#212022", label: "Karty / panely",      group: "background" },
  { name: "bg-secondary",  default: "#2c2c2e", label: "Sekundární plochy",   group: "background" },
  { name: "bg-input",      default: "#2c2c2e", label: "Inputy",              group: "background" },
  { name: "border",        default: "#383839", label: "Hranice / oddělovače", group: "background" },

  // ── Text ─────────────────────────────────────────────────────────────────
  { name: "text-primary",  default: "#e8e6c0", label: "Hlavní text",         group: "text" },
  { name: "text-muted",    default: "#8a8870", label: "Tlumený text",        group: "text" },
  { name: "text-on-accent", default: "#181819", label: "Text na akcentu",    group: "text" },

  // ── Accent (military gold) ───────────────────────────────────────────────
  { name: "accent",        default: "#c8b560", label: "Hlavní akcent (gold)", group: "accent" },
  { name: "accent-light",  default: "#d6c87a", label: "Akcent — světlý",     group: "accent" },
  { name: "accent-dark",   default: "#998644", label: "Akcent — tmavý",      group: "accent" },

  // ── Status ───────────────────────────────────────────────────────────────
  { name: "status-success", default: "#22c55e", label: "Úspěch / OK",         group: "status" },
  { name: "status-warning", default: "#f59e0b", label: "Varování",            group: "status" },
  { name: "status-error",   default: "#ef4444", label: "Chyba / zničení",     group: "status" },
  { name: "status-info",    default: "#3b82f6", label: "Info",                group: "status" },

  // ── Team colors ──────────────────────────────────────────────────────────
  { name: "team-blue",     default: "#3b82f6", label: "Team modrý",           group: "team" },
  { name: "team-green",    default: "#22c55e", label: "Team zelený",          group: "team" },
  { name: "team-gold",     default: "#eab308", label: "Team zlatý",           group: "team" },
  { name: "team-vip",      default: "#a855f7", label: "Team VIP",             group: "team" },
  { name: "team-fallback", default: "#6b7280", label: "Bez teamu",            group: "team" },

  // ── Rank insignia ────────────────────────────────────────────────────────
  { name: "rank-gold",     default: "#ffd700", label: "Rank — zlatá (důstojníci)", group: "rank" },
  { name: "rank-silver",   default: "#c0c0c0", label: "Rank — stříbrná",     group: "rank" },
  { name: "rank-bronze",   default: "#cd7f32", label: "Rank — bronzová",     group: "rank" },
] as const;

/** Validní var name (CSS-safe). */
export type ThemeColorName = (typeof THEME_COLORS)[number]["name"];

/** Map name → default value (pro rychlý lookup). */
export const THEME_DEFAULTS: Record<string, string> = Object.fromEntries(
  THEME_COLORS.map((c) => [c.name, c.default]),
);

// ─── Zaoblení rohů (border-radius) ─────────────────────────────────────────────

/**
 * Konfigurace laditelného zaoblení rohů. Dvě tiery — `radius-control` (tlačítka +
 * ovládací prvky: `rounded`/`sm`/`md`/`lg`) a `radius-surface` (dlaždice/karty/
 * okna/modály: `rounded-xl`/`2xl`). Tailwind je čte přes CSS proměnné
 * `--radius-control` / `--radius-surface` (viz `tailwind.config.ts` + `index.css`).
 * Hodnota je v px; ukládá se jako string `"<n>px"` (stejný override JSON jako barvy).
 */
export interface ThemeRadius {
  /** CSS var name bez `--` prefixu (`radius-control` | `radius-surface`). */
  name: string;
  /** Default v px (číslo). */
  default: number;
  /** Lidsky čitelný popis pro admin UI. */
  label: string;
  /** Doplňkový popis (co všechno ovlivní). */
  hint: string;
  /** Rozsah slideru v px. */
  min: number;
  max: number;
}

export const THEME_RADII: readonly ThemeRadius[] = [
  {
    name: "radius-control",
    default: 8,
    label: "Tlačítka a ovládací prvky",
    hint: "Tlačítka, odkazy-tlačítka, inputy a další drobné ovládací prvky.",
    min: 0,
    max: 24,
  },
  {
    name: "radius-surface",
    default: 12,
    label: "Dlaždice, karty a okna",
    hint: "Dlaždice, karty, panely, modály a dialogy.",
    min: 0,
    max: 32,
  },
] as const;

export type ThemeRadiusName = (typeof THEME_RADII)[number]["name"];

/** Map name → default px-string (`"8px"`) pro diff/porovnání jako u barev. */
export const THEME_RADIUS_DEFAULTS: Record<string, string> = Object.fromEntries(
  THEME_RADII.map((r) => [r.name, `${r.default}px`]),
);

/**
 * Vygeneruje CSS string pro `:root { --xxx: ... }`.
 * Použito v `index.css` (statické defaults) i v `apply-theme.ts` (runtime overrides).
 */
export function themeToCssVars(overrides: Partial<Record<string, string>> = {}): string {
  return THEME_COLORS.map((c) => {
    const value = overrides[c.name] ?? c.default;
    return `  --${c.name}: ${value};`;
  }).join("\n");
}
